import { apiClient } from './apiClient';

export interface Shop {
  id: number;
  name?: string; // Legacy field, use shop_name instead
  shop_name: string;
  slug: string;
  owner_name: string;
  owner_email: string;
  owner_phone: string | null;
  business_type: string;
  address: string | null;
  subscription_status: 'trial' | 'active' | 'expired' | 'suspended';
  payment_status: 'pending' | 'paid' | 'overdue';
  subscription_starts_at: string | null;
  subscription_ends_at: string | null;
  trial_ends_at: string | null;
  next_payment_due: string | null;
  last_payment_at: string | null;
  suspension_reason: string | null;
  is_active: boolean;
  currency: string;
  created_at: string;
  updated_at: string;
  days_remaining?: number;
  owner?: {
    id: number;
    username: string;
    email: string;
    full_name: string;
    last_login_at: string | null;
  };
  stats?: {
    total_users: number;
    total_products: number;
    total_sales: number;
    total_customers: number;
    total_revenue: number;
  };
  users_count?: number;
  products_count?: number;
  sales_count?: number;
}

export interface CreateShopRequest {
  shop_name: string;
  owner_name: string;
  owner_email: string;
  owner_phone?: string;
  business_type: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  trial_days?: number;
  owner_username: string;
  owner_password: string;
  subscription_status?: 'trial' | 'active';
  payment_status?: 'pending' | 'paid' | 'overdue';
  subscription_starts_at?: string;
  subscription_ends_at?: string;
  trial_ends_at?: string;
}

export interface UpdateShopRequest {
  name?: string;
  owner_name?: string;
  owner_email?: string;
  owner_phone?: string;
  subscription_status?: 'trial' | 'active' | 'expired' | 'suspended';
  payment_status?: 'pending' | 'paid' | 'overdue';
  subscription_starts_at?: string;
  subscription_ends_at?: string;
  trial_ends_at?: string;
  trial_days?: number;
}

export interface SuspendShopRequest {
  reason: string;
}

export interface ExtendTrialRequest {
  days: number;
}

export interface RecordPaymentRequest {
  amount: number | string;
  payment_date: string;
  payment_method: string;
  reference?: string;
  subscription_months: number;
  notes?: string;
}

export interface ShopStatistics {
  total_shops: number;
  active_shops: number;
  trial_shops: number;
  suspended_shops: number;
  expired_shops: number;
  total_revenue: number;
  monthly_revenue: number;
  total_earnings: number;
  monthly_earnings: number;
  total_users: number;
  total_products: number;
  total_sales: number;
  growth_data: {
    date: string;
    shops: number;
    revenue: number;
  }[];
  recent_shops: Shop[];
}

class ShopService {
  private baseUrl = '/super-admin/shops';
  
  /**
   * Get all shops with optional filters
   */
  async getShops(params?: {
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<{ data: Shop[]; total: number; current_page: number; last_page: number }> {
    const response = await apiClient.get<any>(this.baseUrl, { params });
    return response.data;
  }

  /**
   * Get a single shop by ID
   */
  async getShop(id: number): Promise<Shop> {
    try {
      // apiClient.get() already returns response.data, not the full response
      const data = await apiClient.get<any>(`${this.baseUrl}/${id}`);
      console.log('Shop API Data:', data);
      
      // Backend returns { shop, owner, stats, days_remaining }
      if (data.shop) {
        const { shop, owner, stats, days_remaining } = data;
        console.log('Merging shop data with owner and stats');
        return {
          ...shop,
          owner: owner,
          stats: stats,
          days_remaining: days_remaining,
        };
      }
      
      // If data is the shop directly (shouldn't happen based on backend code)
      console.log('Returning data as-is');
      return data;
    } catch (error) {
      console.error('Error in getShop:', error);
      throw error;
    }
  }

  /**
   * Create a new shop
   */
  async createShop(data: CreateShopRequest): Promise<Shop> {
    const response = await apiClient.post<any>(this.baseUrl, data);
    return response.data;
  }

  /**
   * Update an existing shop
   */
  async updateShop(id: number, data: UpdateShopRequest): Promise<Shop> {
    const response = await apiClient.put<any>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a shop
   */
  async deleteShop(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Suspend a shop
   */
  async suspendShop(id: number, data: SuspendShopRequest): Promise<Shop> {
    const response = await apiClient.post<any>(`${this.baseUrl}/${id}/suspend`, data);
    return response.data;
  }

  /**
   * Activate a shop
   */
  async activateShop(id: number): Promise<Shop> {
    const response = await apiClient.post<any>(`${this.baseUrl}/${id}/activate`);
    return response.data;
  }

  /**
   * Extend trial period
   */
  async extendTrial(id: number, data: ExtendTrialRequest): Promise<Shop> {
    const response = await apiClient.post<any>(`${this.baseUrl}/${id}/extend-trial`, data);
    return response.data;
  }

  /**
   * Record a payment
   */
  async recordPayment(id: number, data: RecordPaymentRequest): Promise<Shop> {
    const response = await apiClient.post<any>(`${this.baseUrl}/${id}/record-payment`, data);
    return response.data;
  }

  /**
   * Get payment history for a shop
   */
  async getPaymentHistory(id: number): Promise<any[]> {
    const response = await apiClient.get<any>(`${this.baseUrl}/${id}/payment-history`);
    return response.data;
  }

  /**
   * Update a payment record
   */
  async updatePayment(shopId: number, paymentId: number, data: Partial<RecordPaymentRequest>): Promise<any> {
    const response = await apiClient.put<any>(`${this.baseUrl}/${shopId}/payment-history/${paymentId}`, data);
    return response.data;
  }

  /**
   * Delete a payment record
   */
  async deletePayment(shopId: number, paymentId: number): Promise<any> {
    const response = await apiClient.delete<any>(`${this.baseUrl}/${shopId}/payment-history/${paymentId}`);
    return response.data;
  }

  /**
   * Get system-wide statistics
   */
  async getStatistics(): Promise<ShopStatistics> {
    const response = await apiClient.get<any>('/super-admin/statistics');
    return response.data;
  }
}

export const shopService = new ShopService();
